// usePaulinaVersionControls — gestión de VERSIÓN de la feature agent-chat (Paulina).
//
// El contrato FeatureGate (useAgentChatGate) solo expone la INVITACIÓN
// (offered/inviteEligible/showInvite/shown/accept/dismiss). El revert + feedback viven en
// `useFeatureVersion(featureKey)` directamente — mismo reparto que la feature de referencia
// (impresion-ble: invitación por el gate; revert/feedback por useFeatureVersion en
// BlePrintingVersionControls/BlePrintingRolloutOverlays). Este hook empaqueta ESA segunda
// mitad para Paulina:
//   - `canRevert` → ¿se puede ofrecer "Desactivar Paulina"? (entry.can_revert del backend).
//   - `deactivate()` → revert('revert_commit') del rollout (vuelve a 'control': Paulina deja
//     de montarse vía PaulinaGate.offered) + pide el prompt de feedback (signal de módulo,
//     renderizado a nivel host por PaulinaRolloutOverlays — la superficie que lo pide se
//     desmonta con el revert optimista).
//   - `submitRevertFeedback`/`feedbackOptions` → para el prompt de motivos.
//
// Solo root efectivo gestiona la versión del comercio (useEffectiveRoot); el no-root no ve el
// control de desactivar. La defensa REQUIRES_ROOT sigue por detrás (el outbox revierte el
// optimista si el backend rechaza).
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { useEffectiveRoot } from '@hooks/useEffectiveRoot';
import { AGENT_CHAT_FEATURE_KEY } from '@utils/featureRollout/constants';
import { requestRevertFeedback } from '@utils/featureRollout/revertFeedback';
import type { FeedbackOptionsCatalog } from '@utils/featureRollout/types';

export interface PaulinaVersionControls {
  /** Root efectivo Y el backend autoriza revertir → se puede ofrecer "Desactivar Paulina". */
  canDeactivate: boolean;
  /** Ejecuta el revert del rollout (Paulina deja de montarse) y pide el prompt de feedback. */
  deactivate: () => void;
  /** Catálogo de chips per-experimento (ya normalizado) o null → fallback a strings.ts. */
  feedbackOptions: FeedbackOptionsCatalog | null;
  /** Envía el motivo del revert como telemetría fire-and-forget (behavior 'revert_feedback'). */
  submitRevertFeedback: (reason: string, text?: string, trigger?: string) => void;
}

export const usePaulinaVersionControls = (): PaulinaVersionControls => {
  const { canRevert, revert, submitRevertFeedback, feedbackOptions } = useFeatureVersion(AGENT_CHAT_FEATURE_KEY);
  const effectiveRoot = useEffectiveRoot(AGENT_CHAT_FEATURE_KEY);

  // Solo el root efectivo gestiona la versión del comercio; rol sin resolver (undefined) → no
  // ofrecer el control. `canRevert` lo gobierna el backend (cadencia/elegibilidad del revert).
  const canDeactivate = Boolean(effectiveRoot) && canRevert;

  const deactivate = () => {
    // Orden deliberado (igual que BLE): primero el revert del rollout (optimista: Paulina deja
    // de montarse YA y el PUT viaja por el outbox), luego pedimos el prompt de feedback vía
    // signal de módulo (sobrevive al desmontaje de la superficie que lo pidió).
    revert('revert_commit');
    requestRevertFeedback(AGENT_CHAT_FEATURE_KEY);
  };

  return { canDeactivate, deactivate, feedbackOptions, submitRevertFeedback };
};
